import { useCallback, useEffect, useRef } from 'react'

import { produce } from 'immer'

import type { BulkLoads } from '../../../../types'
import { INITIAL_LOAD_FIELD_KEY, INITIAL_LOAD_FIELD_VALUE } from '../constants'

export function useActions(value: BulkLoads, onChange: (value: BulkLoads) => void) {
  const fieldValueRef = useRef(value)
  useEffect(() => {
    fieldValueRef.current = value
  }, [value])

  const onAddLoad = useCallback(() => {
    // Prevent adding the initial load multiple times
    const hasEmptyField = value.exact[INITIAL_LOAD_FIELD_KEY] !== undefined

    if (!hasEmptyField) {
      const data = {
        name: INITIAL_LOAD_FIELD_KEY,
        status: 'exact' as const,
        value: INITIAL_LOAD_FIELD_VALUE,
        addedTimestamp: new Date().getTime(),
      }

      // Add the new load to exact and update loadsDistribution for each order
      const updatedFieldValue = produce(fieldValueRef.current, draft => {
        draft.exact[INITIAL_LOAD_FIELD_KEY] = data
        for (const orderId of Object.keys(draft.loadsDistribution)) {
          draft.loadsDistribution[orderId].push(INITIAL_LOAD_FIELD_KEY)
        }
      })

      onChange(updatedFieldValue)
    }
  }, [onChange, value])

  const onUpdateSelect = useCallback(
    (oldKey: string, newKey: string, warning?: boolean) => {
      if (oldKey === newKey) return

      const loadData = value.exact[oldKey]
      if (!loadData) return

      // Update loadsDistribution for each order - replace old key with new key
      const updatedFieldValue = produce(fieldValueRef.current, draft => {
        // Remove old key and add new key with updated data
        delete draft.exact[oldKey]
        draft.exact[newKey] = { ...loadData, name: newKey, warning: warning ?? false }

        // If newKey exists in partial, remove it
        if (draft.partial[newKey]) {
          delete draft.partial[newKey]
        }

        // Update loadsDistribution for each order
        for (const orderId of Object.keys(draft.loadsDistribution)) {
          const loads = draft.loadsDistribution[orderId]
          const loadIndex = loads.indexOf(oldKey)
          if (loadIndex > -1) {
            loads[loadIndex] = newKey
          }
        }
      })

      onChange(updatedFieldValue)
    },
    [onChange, value],
  )

  const onUpdateValue = useCallback(
    (loadName: string, newValue: string) => {
      const loadData = value.exact[loadName]
      if (!loadData) return

      // Update the value of the load in exact
      const updatedFieldValue = produce(value, draft => {
        draft.exact[loadName] = {
          ...loadData,
          value: newValue === '' ? null : Number(newValue),
          mixed: false,
        }
      })

      onChange(updatedFieldValue)
    },
    [onChange, value],
  )

  const onDeleteLoad = useCallback(
    (loadName: string, type: 'exact' | 'partial') => {
      if (!value[type][loadName]) return

      const updatedFieldValue = produce(value, draft => {
        delete draft[type][loadName]
        // Remove from loadsDistribution for each order
        for (const orderId of Object.keys(draft.loadsDistribution)) {
          const loads = draft.loadsDistribution[orderId]
          const loadIndex = loads.indexOf(loadName)
          if (loadIndex > -1) {
            loads.splice(loadIndex, 1)
          }
        }
      })

      onChange(updatedFieldValue)
    },
    [onChange, value],
  )

  const onAddToAll = useCallback(
    (loadName: string) => {
      const partialLoad = value.partial[loadName]
      if (!partialLoad) return

      const updatedFieldValue = produce(value, draft => {
        // Add to exact with converted status
        draft.exact[loadName] = {
          name: loadName,
          status: 'exact',
          value: null,
          addedTimestamp: Date.now(),
        }
        // Remove from partial
        delete draft.partial[loadName]
        // Add to loadsDistribution for each order that doesn't have it
        for (const orderId of Object.keys(draft.loadsDistribution)) {
          const loads = draft.loadsDistribution[orderId]
          if (!loads.includes(loadName)) {
            loads.push(loadName)
          }
        }
      })

      onChange(updatedFieldValue)
    },
    [onChange, value],
  )

  return {
    onAddLoad,
    onDeleteLoad,
    onUpdateSelect,
    onUpdateValue,
    onAddToAll,
  }
}
